import { type JSX, useEffect, useState } from "react";

import type { Environment, Scope, ScopePatch } from "../api/bench";
import { type ProjectOption, isQuarantine } from "../api/projects";
import type { ProjectsState } from "../hooks/useProjects";
import { generateUlid } from "../lib/ulid";

interface ScopeEditorProps {
  scope: Scope;
  /** Which deployment these headers go to, and whether the tenant is fixed by the token. */
  environment: Environment;
  /** Projects always offered in the picker: configured for this deployment, plus the one in use. */
  projectOptions: string[];
  /** What the Memory Service reports for the tenant -- the picker's main source. */
  projects: ProjectsState;
  onApply: (patch: ScopePatch) => void;
  busy: boolean;
}

/** Chosen when the tester wants a project id the picker does not offer. */
const CUSTOM = "__custom__";
/** Chosen for tenant-level scope, which is a real mode rather than a missing value. */
const NONE = "__none__";

/**
 * The four scope headers every call carries, read-only until you say otherwise.
 *
 * Two things about this panel were confusing enough to be worth the extra click. It looked like a
 * form that was permanently disabled, because with nothing edited there is nothing to apply. And
 * it had two different commit semantics side by side: the text fields were a draft awaiting
 * Apply, while Generate committed a new working session to the server the moment it was pressed.
 *
 * So it now has two explicit modes. Reading shows what the next call will carry. Editing collects
 * a draft -- generated session ids included -- and nothing reaches the server until Save, which
 * sends all four together.
 *
 * Two rules from the service shape what is editable. `X-Tenant-Id` is required on every scoped
 * route and, with authentication on, must equal the token's `tenant_id` claim -- so the tenant is
 * fixed by the identity rather than typed. A *missing* project means tenant-level scope, which is
 * a real mode; and a session id additionally requires a project.
 */
export function ScopeEditor({
  scope,
  environment,
  projectOptions,
  projects,
  onApply,
  busy,
}: ScopeEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(scope);

  // Re-sync whenever the server's view changes, and leave edit mode: the values underneath just
  // moved, so a draft built on the old ones is no longer what the tester meant.
  useEffect(() => {
    setDraft(scope);
    setEditing(false);
  }, [scope]);

  const dirty =
    draft.tenant_id !== scope.tenant_id ||
    draft.project_id !== scope.project_id ||
    draft.agent_id !== scope.agent_id ||
    draft.session_id !== scope.session_id;

  // The tenant is only the tester's to change where the service is not checking a token against
  // it. Editing it under auth-on earns a scope violation, not a different tenant.
  const tenantFixed = environment.auth_enabled;
  const sessionWithoutProject = Boolean(draft.session_id) && !draft.project_id;
  const blocked = busy || !draft.tenant_id.trim() || sessionWithoutProject;

  // What the select offers, in three tiers: the service's own list first (it is the answer to
  // "which projects exist"), then whatever the deployment is configured with, then the value in
  // use, so a saved project never vanishes from its own picker. Quarantine scopes go last: they
  // are real memory nobody chose, worth reaching but not worth confusing with a project.
  const reported = projects.kind === "ready" ? projects.items : [];
  const byId = new Map(reported.map((project) => [project.project_id, project] as const));
  const known = [
    ...new Set(
      [
        ...reported.map((project) => project.project_id),
        ...projectOptions,
        scope.project_id,
        draft.project_id,
      ].filter(isPresent),
    ),
  ];
  const regular = known.filter((id) => !isQuarantine(id));
  const quarantine = known.filter(isQuarantine);
  const [customProject, setCustomProject] = useState(
    () => draft.project_id !== null && !known.includes(draft.project_id),
  );

  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Scope</h2>
        <span className="badge-row">
          {sessionWithoutProject && (
            <span className="flag flag--warn">a session id also needs a project</span>
          )}
          {editing ? (
            <span className="flag flag--warn">
              {dirty ? "unsaved changes" : "editing"}
            </span>
          ) : (
            <span className="flag flag--on">in effect</span>
          )}
        </span>
      </header>

      <p className="muted">
        Every call goes to <strong>{environment.label}</strong> at{" "}
        <code>{environment.ams_base_url}</code>
        {!environment.auth_enabled && (
          <>
            {" "}
            <span className="flag flag--warn" title="Scope comes from headers alone">
              auth off
            </span>
          </>
        )}
        {environment.destructive_allowed && (
          <>
            {" "}
            <span className="flag flag--danger" title="Deletes and resets are permitted here">
              destructive ok
            </span>
          </>
        )}
      </p>

      {editing ? (
        <>
          <div className="form-grid form-grid--tight">
            <label>
              <span>
                Tenant <em>required</em>
              </span>
              <input
                value={draft.tenant_id}
                disabled={tenantFixed}
                onChange={(event) => setDraft({ ...draft, tenant_id: event.target.value })}
              />
              {tenantFixed && (
                <small className="muted">
                  Fixed by the identity: the service requires this to match the token&rsquo;s
                  <code> tenant_id</code> claim. To test another tenant you need a token for it.
                </small>
              )}
            </label>

            <label>
              <span>Project</span>
              <select
                value={
                  customProject ? CUSTOM : draft.project_id === null ? NONE : draft.project_id
                }
                onChange={(event) => {
                  const chosen = event.target.value;
                  if (chosen === CUSTOM) {
                    setCustomProject(true);
                    return;
                  }
                  setCustomProject(false);
                  setDraft({ ...draft, project_id: chosen === NONE ? null : chosen });
                }}
              >
                <option value={NONE}>tenant-level (no project)</option>
                {regular.map((project) => (
                  <option key={project} value={project}>
                    {describeProject(project, byId.get(project))}
                  </option>
                ))}
                {quarantine.length > 0 && (
                  <optgroup label="Quarantine (runner scope fallbacks)">
                    {quarantine.map((project) => (
                      <option key={project} value={project}>
                        {describeProject(project, byId.get(project))}
                      </option>
                    ))}
                  </optgroup>
                )}
                {/* The list can be stale or unreachable, and a project exists the moment a runner
                    first writes under it -- so an unseen project must still be typeable. */}
                <option value={CUSTOM}>another project…</option>
              </select>
              {customProject && (
                <input
                  value={draft.project_id ?? ""}
                  placeholder="project id"
                  onChange={(event) =>
                    setDraft({ ...draft, project_id: event.target.value || null })
                  }
                />
              )}
              {projects.kind === "loading" && (
                <small className="muted">Reading the project list…</small>
              )}
              {projects.kind === "ready" && projects.detail && (
                <small className="muted">{projects.detail}</small>
              )}
              {projects.kind === "error" && (
                <small className="muted">Project list unavailable: {projects.message}</small>
              )}
              <small className="muted">
                Required for entities, code, SDLC, sessions, sources, ask, ingest and project
                admin. Tenant-level is a real mode, not a missing value.
              </small>
            </label>

            <label>
              <span>Agent</span>
              <input
                value={draft.agent_id ?? ""}
                placeholder="none"
                onChange={(event) => setDraft({ ...draft, agent_id: event.target.value || null })}
              />
              <small className="muted">
                Whose memories a write belongs to. Any id; it is not looked up.
              </small>
            </label>

            <label>
              <span>Working session</span>
              <span className="input-row">
                <input value={draft.session_id ?? ""} placeholder="none" readOnly />
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, session_id: generateUlid() })}
                  disabled={busy}
                >
                  Generate
                </button>
                {draft.session_id && (
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, session_id: null })}
                    disabled={busy}
                  >
                    Clear
                  </button>
                )}
              </span>
              <small className="muted">
                A ULID; the Memory Service has no session-create call, so the caller mints one.
                Generated into this draft &mdash; it is not in use until you save.
              </small>
            </label>
          </div>

          <div className="panel__actions">
            <button
              type="button"
              className="primary"
              disabled={blocked || !dirty}
              title={
                sessionWithoutProject
                  ? "A session id needs a project"
                  : dirty
                    ? undefined
                    : "Nothing has changed"
              }
              onClick={() =>
                onApply({
                  tenant_id: draft.tenant_id,
                  project_id: draft.project_id ?? "",
                  agent_id: draft.agent_id ?? "",
                  session_id: draft.session_id ?? "",
                })
              }
            >
              Save scope
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setDraft(scope);
                setCustomProject(false);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <dl className="scope-summary">
            <div>
              <dt>Tenant</dt>
              <dd>{scope.tenant_id}</dd>
            </div>
            <div>
              <dt>Project</dt>
              <dd>{scope.project_id ?? <em>tenant-level</em>}</dd>
            </div>
            <div>
              <dt>Agent</dt>
              <dd>{scope.agent_id ?? <em>none</em>}</dd>
            </div>
            <div>
              <dt>Working session</dt>
              <dd>{scope.session_id ?? <em>none</em>}</dd>
            </div>
          </dl>

          <div className="panel__actions">
            <button type="button" onClick={() => setEditing(true)} disabled={busy}>
              Edit scope
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function isPresent(value: string | null): value is string {
  return typeof value === "string" && value.length > 0;
}

/** The option label: the id, and where the service reported it, how much lives there. */
function describeProject(projectId: string, reported: ProjectOption | undefined): string {
  const tag = isQuarantine(projectId) ? "quarantine, " : "";
  if (!reported) {
    return `${projectId} (${tag}configured)`;
  }
  const memories = plural(reported.memory_count, "memory", "memories");
  const repositories =
    reported.repository_count > 0
      ? `, ${plural(reported.repository_count, "repository", "repositories")}`
      : "";
  return `${projectId} — ${tag}${memories}${repositories}`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
